import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { User, Lock, Mail, UserPlus, LogIn, Shield, KeyRound } from "lucide-react";

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1 = enter email, 2 = enter code & new password
  const [resetCode, setResetCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "technician",
    adminCode: "",
  });

  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        await register(
          formData.email, 
          formData.password, 
          formData.name, 
          formData.role,
          formData.role === "admin" ? formData.adminCode : null
        );
        toast.success("Account created successfully!");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Authentication failed";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/request-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await response.json();
      if (data.reset_code) {
        setResetCode(data.reset_code);
        toast.success(`Reset code: ${data.reset_code}`);
      }
      setResetStep(2);
      toast.success("Enter the reset code to continue");
    } catch (error) {
      toast.error("Failed to request reset");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email,
          reset_code: formData.adminCode, // reusing adminCode field for reset code
          new_password: formData.password
        })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Reset failed");
      }
      toast.success("Password reset successful! Please login.");
      setIsResetMode(false);
      setResetStep(1);
      setFormData({ ...formData, password: "", adminCode: "" });
    } catch (error) {
      toast.error(error.message || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-[#003366] flex flex-col">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-[#003366] font-oswald font-bold text-xl">BB</span>
          </div>
          <div>
            <h1 className="font-oswald font-bold text-white text-2xl tracking-tight">BLUE BEAR</h1>
            <p className="text-blue-200 text-sm">Technician Tool</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-8">
        <Card className="w-full max-w-md shadow-2xl border-0 animate-fade-in">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-oswald text-3xl text-[#003366] tracking-tight">
              {isResetMode ? "RESET PASSWORD" : isLogin ? "WELCOME BACK" : "CREATE ACCOUNT"}
            </CardTitle>
            <CardDescription className="text-slate-500">
              {isResetMode 
                ? resetStep === 1 ? "Enter your email to receive a reset code" : "Enter the code and your new password"
                : isLogin ? "Sign in to access your dashboard" : "Register to get started"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isResetMode ? (
              // Password Reset Form
              <form onSubmit={resetStep === 1 ? handleRequestReset : handleResetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="h-14 pl-12 text-lg rounded-xl border-2 focus:border-[#003366]"
                      data-testid="reset-email-input"
                      required
                      disabled={resetStep === 2}
                    />
                  </div>
                </div>

                {resetStep === 2 && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="resetCode" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Reset Code
                      </Label>
                      <div className="relative">
                        <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                          id="resetCode"
                          name="adminCode"
                          type="text"
                          placeholder="Enter 6-digit code"
                          value={formData.adminCode}
                          onChange={handleChange}
                          className="h-14 pl-12 text-lg rounded-xl border-2 focus:border-[#003366]"
                          data-testid="reset-code-input"
                          required
                        />
                      </div>
                      {resetCode && (
                        <p className="text-xs text-green-600 font-medium">Your reset code: {resetCode}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        New Password
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={handleChange}
                          className="h-14 pl-12 text-lg rounded-xl border-2 focus:border-[#003366]"
                          data-testid="new-password-input"
                          required
                        />
                      </div>
                    </div>
                  </>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-14 text-lg font-bold uppercase tracking-wide bg-[#FF6B00] hover:bg-[#e56000] text-white rounded-xl shadow-lg active:scale-95 transition-transform"
                  data-testid="reset-submit-btn"
                >
                  {isLoading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full spinner" />
                  ) : resetStep === 1 ? (
                    "Send Reset Code"
                  ) : (
                    "Reset Password"
                  )}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setIsResetMode(false); setResetStep(1); }}
                    className="text-[#003366] font-medium hover:underline"
                  >
                    Back to Sign In
                  </button>
                </div>
              </form>
            ) : (
              // Login/Register Form
              <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Full Name
                  </Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={handleChange}
                      className="h-14 pl-12 text-lg rounded-xl border-2 focus:border-[#003366]"
                      data-testid="register-name-input"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="h-14 pl-12 text-lg rounded-xl border-2 focus:border-[#003366]"
                    data-testid="email-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="h-14 pl-12 text-lg rounded-xl border-2 focus:border-[#003366]"
                    data-testid="password-input"
                    required
                  />
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Account Type
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, role: "technician", adminCode: "" })}
                      className={`h-14 rounded-xl border-2 font-medium transition-colors ${
                        formData.role === "technician"
                          ? "border-[#003366] bg-[#003366] text-white"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid="role-technician-btn"
                    >
                      Technician
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, role: "admin" })}
                      className={`h-14 rounded-xl border-2 font-medium transition-colors ${
                        formData.role === "admin"
                          ? "border-[#003366] bg-[#003366] text-white"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid="role-admin-btn"
                    >
                      Admin
                    </button>
                  </div>
                </div>
              )}

              {!isLogin && formData.role === "admin" && (
                <div className="space-y-2">
                  <Label htmlFor="adminCode" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Admin Code
                  </Label>
                  <div className="relative">
                    <Shield className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="adminCode"
                      name="adminCode"
                      type="password"
                      placeholder="Enter admin code"
                      value={formData.adminCode}
                      onChange={handleChange}
                      className="h-14 pl-12 text-lg rounded-xl border-2 focus:border-[#003366]"
                      data-testid="admin-code-input"
                      required={formData.role === "admin"}
                    />
                  </div>
                  <p className="text-xs text-slate-500">Contact your manager for the admin code</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-14 text-lg font-bold uppercase tracking-wide bg-[#FF6B00] hover:bg-[#e56000] text-white rounded-xl shadow-lg active:scale-95 transition-transform"
                data-testid="submit-btn"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full spinner" />
                ) : isLogin ? (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    Sign In
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5 mr-2" />
                    Create Account
                  </>
                )}
              </Button>
            
            <div className="mt-6 text-center space-y-2">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#003366] font-medium hover:underline block w-full"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Need an account? Register" : "Already have an account? Sign In"}
              </button>
              {isLogin && (
                <button
                  onClick={() => setIsResetMode(true)}
                  className="text-slate-500 text-sm hover:underline block w-full"
                  data-testid="forgot-password-btn"
                >
                  Forgot password?
                </button>
              )}
            </div>
            </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
